// @ts-check
import { useCallback, useEffect, useRef, useState } from 'react';
import MfaSetupModal from '../auth/MfaSetupModal.jsx';
import {
  callStepUpVerify,
  callSuperAdminMfaEnrollStart,
  callSuperAdminMfaSetup,
} from '../../api/mfaApi.js';
import { getSessionToken } from '../../api/sessionToken.js';
import { GOOGLE_CLIENT_ID, useGoogleSignInButton } from './useGoogleSignInButton.js';

/**
 * The super-admin step-up API for the shared StepUpDialog (FDNA-02b).
 *
 * step-up-verify routes a super-admin session to its super-admin branch, so
 * begin/verify are the tenant calls with the super-admin session. Enrolment
 * differs: identity is re-proved by a FRESH Google sign-in (D-b1 = A) or the
 * account password, against superadmin-mfa-enroll, and the factor is
 * registered through superadmin-mfa-setup, which issues no new session.
 *
 * @type {import('../auth/StepUpDialog.jsx').StepUpApi}
 */
export const superAdminStepUpApi = {
  begin: () => callStepUpVerify('begin', {}, getSessionToken()),
  verify: (body) => callStepUpVerify('verify', body, getSessionToken()),
  enrolStart: (password) => callSuperAdminMfaEnrollStart({ password }, getSessionToken()),
  adoptSession: () => {
    /* super-admin enrolment keeps the current session */
  },
  renderEnrolProof: ({ onChallenge, onCancel }) => (
    <SuperAdminEnrolProof onChallenge={onChallenge} onCancel={onCancel} />
  ),
  renderEnrolSetup: ({ challengeToken, onDone, onCancel }) => (
    <MfaSetupModal
      challengeToken={challengeToken}
      functionsBaseUrl={undefined}
      callSetup={(/** @type {string} */ action, /** @type {object} */ body) =>
        callSuperAdminMfaSetup(action, body, getSessionToken())
      }
      onComplete={onDone}
      onBack={onCancel}
    />
  ),
  recoveryHint:
    'Your authenticator cannot be used. Ask another super-admin to reset your two-step verification (Super-admins page). If you are the only super-admin, follow the break-glass procedure in docs/authentication.md.',
};

/**
 * Identity proof before a super-admin registers a factor: sign in with Google
 * again (the proof for a Google-only account), or enter the account password.
 *
 * @param {{ onChallenge: (challengeToken: string) => void, onCancel: () => void }} props
 */
function SuperAdminEnrolProof({ onChallenge, onCancel }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  /** @type {import('react').MutableRefObject<HTMLDivElement | null>} */
  const googleBtnRef = useRef(null);
  // A server-issued, single-use nonce bound into Google's ID token: only a
  // sign-in made for THIS enrolment proves identity, never an earlier one.
  const [google, setGoogle] = useState(
    /** @type {{ nonce: string, nonceToken: string } | null} */ (null)
  );
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return undefined;
    let live = true;
    callSuperAdminMfaEnrollStart({ action: 'google-nonce' }, getSessionToken()).then((res) => {
      if (live && res.nonce && res.nonceToken)
        setGoogle({ nonce: res.nonce, nonceToken: res.nonceToken });
    });
    return () => {
      live = false;
    };
  }, []);

  const prove = useCallback(
    async (
      /** @type {{ password?: string, googleCredential?: string, nonceToken?: string }} */ proof
    ) => {
      setBusy(true);
      setError('');
      const res = await callSuperAdminMfaEnrollStart(proof, getSessionToken());
      setBusy(false);
      setPassword('');
      if (res.error || !res.challengeToken) {
        setError(res.error || 'Could not confirm your identity. Please try again.');
        return;
      }
      onChallenge(res.challengeToken);
    },
    [onChallenge]
  );

  const onGoogle = useCallback(
    (/** @type {{ credential: string }} */ response) =>
      prove({ googleCredential: response.credential, nonceToken: google?.nonceToken }),
    [prove, google]
  );
  const googleReady = useGoogleSignInButton(googleBtnRef, onGoogle, {
    text: 'continue_with',
    width: 320,
    nonce: google?.nonce,
    enabled: !!google,
  });

  const hint = {
    fontSize: 12,
    color: 'var(--fg3)',
    fontFamily: 'var(--font-mono)',
    marginBottom: 16,
    lineHeight: 1.5,
  };

  return (
    <div data-testid="step-up-enrol-proof">
      {error && (
        <div
          role="alert"
          style={{
            padding: '8px 12px',
            marginBottom: 12,
            borderRadius: 8,
            background: 'var(--bg3)',
            border: '1px solid var(--danger)',
            color: 'var(--danger)',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}
      <p style={hint}>
        This action needs two-step verification, which is not set up on your super-admin account
        yet. Confirm it is you to set it up now.
      </p>
      {GOOGLE_CLIENT_ID && (
        <div style={{ marginBottom: 16 }}>
          <p style={hint}>Sign in with Google again:</p>
          {!googleReady && <p style={hint}>Loading…</p>}
          <div ref={googleBtnRef} style={{ display: googleReady && !busy ? 'flex' : 'none' }} />
        </div>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!password) {
            setError('Enter your password to continue.');
            return;
          }
          prove({ password });
        }}
      >
        <p style={hint}>{GOOGLE_CLIENT_ID ? 'Or enter your password:' : 'Enter your password:'}</p>
        <input
          className="input"
          data-testid="step-up-password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          aria-label="Password"
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            data-testid="step-up-confirm"
            disabled={busy}
          >
            {busy ? 'Verifying…' : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  );
}
